import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react"
import { ChevronDown, Plus } from "lucide-react"
import {
  ComponentPropsWithoutRef,
  FocusEvent,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from "react"

import { classNames } from "../../../../lib/classNames"
import {
  HEADLESS_OPTIONS_LIMIT,
  isHeadlessOptionsTruncated,
  renderHeadlessSafeItems,
} from "../../../../lib/headless"
import { Icon, IconSizes } from "../../../Icon"
import { TextInput, TextInputSize } from "../text/TextInput"

const truncationOption = Symbol("truncation")
const createOption = Symbol("create")
const noResultsOption = Symbol("noResults")

type SpecialOption =
  | typeof truncationOption
  | typeof createOption
  | typeof noResultsOption
type OptionValue<T> = T | null | SpecialOption

function isSpecialOption<T>(option: OptionValue<T>): option is SpecialOption {
  return (
    option === truncationOption ||
    option === createOption ||
    option === noResultsOption
  )
}

const defaultOptionClassName =
  "bg-neutral-default relative cursor-default select-none px-4 py-2"

function renderTruncationOption(maxOptions: number) {
  return (
    <ComboboxOption
      key="truncation"
      value={truncationOption}
      disabled
      className={classNames(
        defaultOptionClassName,
        "text-body-sm color-subtle w-full text-center"
      )}
    >
      Showing the first {maxOptions.toLocaleString()} results. Type to narrow
      the list.
    </ComboboxOption>
  )
}

function renderNoResultsOption() {
  return (
    <ComboboxOption
      key="no-results"
      value={noResultsOption}
      disabled
      className={classNames(
        defaultOptionClassName,
        "text-body-lg w-full text-center"
      )}
    >
      No results
    </ComboboxOption>
  )
}

function renderCreateOption(name: string) {
  return (
    <ComboboxOption
      key="create"
      value={createOption}
      className={({ focus }) =>
        classNames(
          defaultOptionClassName,
          "w-full",
          focus && "bg-neutral-hovered"
        )
      }
    >
      <div className="flex flex-row items-center gap-2">
        <Icon
          icon={Plus}
          size={IconSizes.ExtraSmall}
          className="color-icon-default"
          aria-hidden="true"
        />
        <span className="text-body-lg block truncate">
          Create new{" "}
          <span className="color-accent-blue font-semibold">
            &quot;{name}&quot;
          </span>
        </span>
      </div>
    </ComboboxOption>
  )
}

export interface ComboboxProps<T>
  extends Omit<ComponentPropsWithoutRef<"div">, "onChange"> {
  label?: string
  secondaryLabel?: string
  items: T[]
  maxOptions?: number
  selectedItem: T | null
  onSelectItem: (item: T | null) => void
  buildItemFromNew?: (newItem: string) => T
  isCreatable?: boolean
  isVirtualized?: boolean
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  filterValueExtractor?: (item: T) => string
  invalid?: boolean
  errorMessage?: string
  disabled?: boolean
  placeholder?: string
  onChangeQuery?: (query: string) => void
  autoFocus?: boolean
  hideChevronButton?: boolean
  eq?: (a: T | null, b: T | null) => boolean
  isOpenOnFocus?: boolean
  renderItemPrefix?: (a: T | null) => ReactElement
  renderItemLabel?: (a: T | null) => ReactNode
  optionsAnchor?: ComponentPropsWithoutRef<typeof ComboboxOptions>["anchor"]
  optionsClassName?: string
  inputClassName?: string
  isDeselectable?: boolean
  deselectText?: string
  isBold?: boolean
  inputSize?: TextInputSize
}

export function Combobox<T>(props: ComboboxProps<T>) {
  const [query, setQuery] = useState("")

  const {
    label,
    secondaryLabel,
    items,
    maxOptions = HEADLESS_OPTIONS_LIMIT,
    selectedItem,
    onSelectItem,
    buildItemFromNew = (newItem) => newItem,
    isCreatable,
    isVirtualized = false,
    keyExtractor,
    valueExtractor,
    filterValueExtractor,
    invalid,
    errorMessage,
    disabled,
    placeholder,
    onChangeQuery,
    autoFocus,
    hideChevronButton = false,
    isOpenOnFocus = false,
    renderItemPrefix,
    renderItemLabel,
    eq,
    optionsAnchor,
    optionsClassName,
    inputClassName,
    isDeselectable,
    isBold,
    deselectText,
    inputSize = "md",
    ...otherProps
  } = props

  const searchExtractor = filterValueExtractor ?? valueExtractor
  const filterFn = useMemo(
    () =>
      onChangeQuery
        ? () => () => true
        : (query: string) => (item: T) =>
            searchExtractor(item).toLowerCase().includes(query.toLowerCase()),
    [onChangeQuery, searchExtractor]
  )
  const filteredItems = useMemo(
    () => (query === "" ? items : items.filter(filterFn(query))),
    [items, query, filterFn]
  )

  const equalityFn = (a: OptionValue<T>, b: OptionValue<T>) => {
    if (isSpecialOption(a) || isSpecialOption(b)) return a === b
    if (eq) return eq(a, b)
    if (a === null && b === null) return true
    if (a === null || b === null) return false
    return keyExtractor(a) === keyExtractor(b)
  }

  // While the query is empty, seed the "new item" option from a selected item
  // that isn't in items yet (typically committed on a previous open but not
  // persisted), so reopening still shows it as the pending new entry. Only
  // valid with local filtering — under onChangeQuery, items hold the last
  // search's results, so absence doesn't mean the selection is new.
  const pendingNewItemName =
    !onChangeQuery &&
    selectedItem &&
    !items.some((item) => equalityFn(item, selectedItem))
      ? valueExtractor(selectedItem)
      : ""
  const newItemName = query !== "" ? query : pendingNewItemName

  const findItemByName = (name: string) =>
    items.find(
      (item) => valueExtractor(item).toLowerCase() === name.toLowerCase()
    )

  const showNewItemOption =
    !!isCreatable && newItemName !== "" && !findItemByName(newItemName)

  const options = useMemo((): OptionValue<T>[] => {
    const isTruncated =
      isVirtualized && isHeadlessOptionsTruncated(filteredItems, maxOptions)
    const result: OptionValue<T>[] = [
      ...(isDeselectable ? [null] : []),
      ...renderHeadlessSafeItems(filteredItems, maxOptions),
      ...(isTruncated ? ([truncationOption] as const) : []),
      ...(showNewItemOption ? ([createOption] as const) : []),
    ]
    // Headless UI's virtual list renders nothing for an empty options array.
    return isVirtualized && result.length === 0 ? [noResultsOption] : result
  }, [
    filteredItems,
    maxOptions,
    isVirtualized,
    isDeselectable,
    showNewItemOption,
  ])

  const hasNoResults =
    filteredItems.length === 0 && !showNewItemOption && !isDeselectable

  // For creatable comboboxes, leaving the field with uncommitted text should
  // select it, not discard it — Headless UI's default blur behavior silently
  // resets the input to the last selected value.
  const skipBlurCommitRef = useRef(false)
  const commitTypedText = (text: string) => {
    const typed = text.trim()
    if (!typed) return
    if (selectedItem && valueExtractor(selectedItem) === typed) return
    onSelectItem(findItemByName(typed) ?? (buildItemFromNew(typed) as T))
  }
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Tab" || !isCreatable) return
    // Commit before Headless UI's own Tab handler runs; if the user arrowed
    // to an option, that handler selects it afterwards and wins, matching
    // Enter's semantics.
    commitTypedText(event.currentTarget.value)
    skipBlurCommitRef.current = true
  }
  const onInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const skip = skipBlurCommitRef.current
    skipBlurCommitRef.current = false
    if (skip || !isCreatable) return
    const text = event.currentTarget.value
    // Headless UI's own blur handler runs after this one and resets an empty
    // selection via onChange(null); defer the commit so it lands last.
    queueMicrotask(() => commitTypedText(text))
  }

  function renderOption(option: OptionValue<T>) {
    if (option === truncationOption) return renderTruncationOption(maxOptions)
    if (option === noResultsOption) return renderNoResultsOption()
    if (option === createOption) return renderCreateOption(newItemName)
    return (
      <ComboboxOption
        key={option === null ? "deselect" : `item:${keyExtractor(option)}`}
        value={option}
        className={({ focus, selected }) =>
          classNames(
            defaultOptionClassName,
            isVirtualized && "w-full",
            focus && "bg-neutral-hovered",
            selected && "bg-selected-alternate"
          )
        }
      >
        <div className="flex flex-row items-center gap-2">
          {option === null ? (
            <span className="text-body-lg block truncate">
              {deselectText ?? "None"}
            </span>
          ) : (
            <>
              {renderItemPrefix && renderItemPrefix(option)}
              {renderItemLabel ? (
                renderItemLabel(option)
              ) : (
                <span className="text-body-lg block truncate">
                  {valueExtractor(option)}
                </span>
              )}
            </>
          )}
        </div>
      </ComboboxOption>
    )
  }

  return (
    <div {...otherProps}>
      <HeadlessCombobox<OptionValue<T>>
        value={selectedItem}
        virtual={
          isVirtualized
            ? {
                options,
                disabled: (option) =>
                  option === truncationOption || option === noResultsOption,
              }
            : null
        }
        onChange={(option) => {
          if (option === createOption) {
            onSelectItem(buildItemFromNew(newItemName) as T)
            return
          }
          if (!isSpecialOption(option)) onSelectItem(option)
        }}
        disabled={disabled}
        by={equalityFn}
        multiple={false}
        immediate={isOpenOnFocus}
        onClose={() => setQuery("")}
      >
        <ComboboxInput
          as={TextInput}
          label={label}
          secondaryLabel={secondaryLabel}
          onChange={(event) => {
            setQuery(event.target.value)
            onChangeQuery?.(event.target.value)
          }}
          onKeyDown={onInputKeyDown}
          onBlur={onInputBlur}
          displayValue={(item) =>
            item ? valueExtractor(item as unknown as T) : ""
          }
          placeholder={placeholder ?? "Select one"}
          autoFocus={autoFocus}
          errorMessage={errorMessage}
          invalid={invalid}
          className={classNames(
            "block truncate",
            isBold && "font-semibold",
            inputClassName
          )}
          inputSize={inputSize}
          rightElement={
            !hideChevronButton ? (
              <ComboboxButton>
                <Icon
                  icon={ChevronDown}
                  size={IconSizes.ExtraSmall}
                  className="color-icon-default"
                  aria-hidden="true"
                />
              </ComboboxButton>
            ) : undefined
          }
        />

        <ComboboxOptions
          anchor={
            optionsAnchor ?? {
              to: "bottom",
              gap: 8,
            }
          }
          className={classNames(
            "w-(--input-width) elevation-surface-default border-v2-border-default shadow-control text-body-lg z-10 max-h-60 overflow-auto rounded-sm border [--anchor-max-height:15rem]",
            optionsClassName
          )}
          modal={false}
        >
          {isVirtualized ? (
            ({ option }) => renderOption(option)
          ) : hasNoResults ? (
            <div
              className={classNames(
                defaultOptionClassName,
                "text-body-lg text-center"
              )}
            >
              No results
            </div>
          ) : (
            <>
              {options
                .slice(0, showNewItemOption ? -1 : undefined)
                .map(renderOption)}
              {isHeadlessOptionsTruncated(filteredItems, maxOptions) && (
                <div
                  className={classNames(
                    defaultOptionClassName,
                    "text-body-sm color-subtle text-center"
                  )}
                >
                  Showing the first {maxOptions.toLocaleString()} results. Type
                  to narrow the list.
                </div>
              )}
              {showNewItemOption && renderCreateOption(newItemName)}
            </>
          )}
        </ComboboxOptions>
      </HeadlessCombobox>
    </div>
  )
}
